import { useMutation, useQueryClient } from '@tanstack/react-query'
import apiClient from '@/api/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Switch } from '@/components/ui/switch'
import { Label } from '@/components/ui/label'
import { useAllSettings, useKitchenFlowEnabled } from '@/hooks/useKitchenFlowEnabled'
import { getStoredUserRole } from '@/lib/staff-roles'
import { parsePOApprovers } from './purchasingLogic'
import { parseSelfApproval } from './purchasingPilotLogic'
import { PURCHASING_STRINGS } from './purchasingStrings'

const S = PURCHASING_STRINGS.approvers

/** Kitchen flow lane D: admin-only switch for app_settings.po_approvers.
 *  Off = ["admin"] (the default everywhere); on = ["admin","inventory_manager"].
 *  Renders nothing without the kitchen flow or for a non-admin. */
export function PoApproversSettingCard({ showToast }: { showToast?: (t: 'success' | 'error', m: string) => void }) {
  const enabled = useKitchenFlowEnabled()
  const all = useAllSettings()
  const qc = useQueryClient()
  const approvers = parsePOApprovers(all?.po_approvers)
  const on = approvers.includes('inventory_manager')
  const save = useMutation({
    mutationFn: (next: boolean) => apiClient.updateSetting('po_approvers', next ? ['admin', 'inventory_manager'] : ['admin']),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'all'] })
      showToast?.('success', S.saved)
    },
    onError: (e: Error) => showToast?.('error', e.message),
  })
  // Pilot Batch 5: app_settings.po_self_approval (missing = on).
  const selfOn = parseSelfApproval(all?.po_self_approval)
  const saveSelf = useMutation({
    mutationFn: (next: boolean) => apiClient.updateSetting('po_self_approval', next),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['settings', 'all'] })
      showToast?.('success', S.saved)
    },
    onError: (e: Error) => showToast?.('error', e.message),
  })
  if (!enabled || getStoredUserRole() !== 'admin') return null
  return (
    <Card>
      <CardHeader className="pb-4">
        <CardTitle className="text-base">{S.title}</CardTitle>
        <CardDescription>{S.help}</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-3">
          <Switch id="po-approvers-im" checked={on} disabled={save.isPending} onCheckedChange={(v) => save.mutate(v)} />
          <Label htmlFor="po-approvers-im">{S.toggle}</Label>
        </div>
        <div className="space-y-1">
          <div className="flex items-center gap-3">
            <Switch id="po-self-approval" checked={selfOn} disabled={saveSelf.isPending} onCheckedChange={(v) => saveSelf.mutate(v)} />
            <Label htmlFor="po-self-approval">{S.selfToggle}</Label>
          </div>
          <p className="text-sm text-muted-foreground">{S.selfHelp}</p>
        </div>
      </CardContent>
    </Card>
  )
}
